import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXTwitter, faInstagram } from '@fortawesome/free-brands-svg-icons';
import { faDatabase, faIndustry, faLayerGroup, faBrain, faUserCheck, faMagnifyingGlass, faChartPie, faBuildingColumns, faCubes, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { COLORS, BREAKPOINTS } from '../../constants/theme';

const Section = styled.section`
  padding: 4rem 2rem 6rem;
  background: rgba(0, 0, 0, 0.2);
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.5rem);
  text-align: center;
  margin-bottom: 1rem;
  color: ${COLORS.pink};
`;

const Subtitle = styled.p`
  text-align: center;
  color: ${COLORS.whiteTransparent};
  max-width: 800px;
  margin: 0 auto 3rem;
  line-height: 1.6;
  font-size: 1.05rem;
`;

const PlatformGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2.5rem;
  max-width: 1200px;
  margin: 0 auto 3rem;
  @media (max-width: ${BREAKPOINTS.mobile}) { grid-template-columns: 1fr; }
`;

const Card = styled(motion.div)`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 2rem;
  &:hover { border-color: rgba(255, 255, 255, 0.15); }
`;

const CardHeader = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: ${COLORS.cyan};
  svg { font-size: 1.3rem; }
`;

const Step = styled.div`
  display: flex;
  gap: 0.8rem;
  margin-bottom: 1.2rem;
  &:last-child { margin-bottom: 0; }
`;

const StepIcon = styled.div`
  width: 32px; height: 32px; min-width: 32px;
  background: ${COLORS.gradientPrimary};
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 0.85rem; margin-top: 2px;
`;

const StepText = styled.div`
  h4 { color: ${COLORS.white}; font-size: 0.95rem; margin-bottom: 0.3rem; }
  p { color: ${COLORS.whiteTransparent}; font-size: 0.88rem; line-height: 1.5; }
`;

const OutputTitle = styled.h3`
  color: ${COLORS.cyan};
  font-size: 1.3rem;
  text-align: center;
  margin-bottom: 1.5rem;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
`;

const OutputGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.6rem 2rem;
  @media (max-width: ${BREAKPOINTS.mobile}) { grid-template-columns: 1fr; }
`;

const OutputItem = styled.div`
  color: ${COLORS.whiteTransparent};
  font-size: 0.88rem;
  line-height: 1.5;
  padding: 0.4rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  &::before { content: '\\2713'; color: ${COLORS.cyan}; margin-right: 0.5rem; font-weight: bold; }
`;

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true } };
const twitterIcons = [faDatabase, faIndustry, faLayerGroup, faBrain, faUserCheck];
const instagramIcons = [faMagnifyingGlass, faChartPie, faBuildingColumns, faCubes, faUserCheck];

function PipelineCard({ brandIcon, titleKey, prefix, icons, delay, t }) {
  return (
    <Card {...fadeUp} transition={{ duration: 0.5, delay }}>
      <CardHeader>
        <FontAwesomeIcon icon={brandIcon} /> {t(titleKey)}
      </CardHeader>
      {icons.map((icon, i) => (
        <Step key={i}>
          <StepIcon><FontAwesomeIcon icon={icon} /></StepIcon>
          <StepText>
            <h4>{i + 1}. {t(`landing.${prefix}Step${i + 1}Title`)}</h4>
            <p>{t(`landing.${prefix}Step${i + 1}Desc`)}</p>
          </StepText>
        </Step>
      ))}
    </Card>
  );
}

function MethodologySection() {
  const { t } = useTranslation();
  const outputs = Array.from({ length: 8 }, (_, i) => t(`landing.methodologyOutput${i + 1}`));

  return (
    <Section>
      <Title>{t('landing.methodologyTitle')}</Title>
      <Subtitle>{t('landing.methodologySubtitle')}</Subtitle>
      <PlatformGrid>
        <PipelineCard brandIcon={faXTwitter} titleKey="landing.methodologyTwitterTitle" prefix="methodTwitter" icons={twitterIcons} delay={0} t={t} />
        <PipelineCard brandIcon={faInstagram} titleKey="landing.methodologyInstagramTitle" prefix="methodInstagram" icons={instagramIcons} delay={0.2} t={t} />
      </PlatformGrid>
      <Card {...fadeUp} transition={{ duration: 0.5, delay: 0.3 }} style={{ maxWidth: 900, margin: '0 auto' }}>
        <OutputTitle>
          <FontAwesomeIcon icon={faClipboardList} /> {t('landing.methodologyOutputTitle')}
        </OutputTitle>
        <OutputGrid>
          {outputs.map((item, i) => <OutputItem key={i}>{item}</OutputItem>)}
        </OutputGrid>
      </Card>
    </Section>
  );
}

export default MethodologySection;
